/* ============================================================
   OBEYTHECULTURE  EDITORIAL  Dopamine Interactions
   ============================================================
   Heavy GSAP usage:
   1. Hero entrance (zoom + masked text reveal)
   2. Pinned scroll sequence (3 cross-fading frames)
   3. Horizontal lookbook (ScrollTrigger scrub)
   4. Kinetic typography (word-by-word reveals)
   5. Parallax image grid (multi-speed scrub)
   6. Section reveals & simple fades
   7. CTA entrance
   ============================================================ */
(() => {
  'use strict';

  const gsap = window.gsap;
  const ST = window.ScrollTrigger;
  if (!gsap || !ST) return;
  gsap.registerPlugin(ST);

  // const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  // if (prefersReduced) return;

  /* 
     UTILITY: Split text into per-word spans for stagger
      */
  function splitWords(el) {
    const words = el.textContent.trim().split(/\s+/);
    el.textContent = '';
    const inners = [];
    words.forEach((w, i) => {
      const wrap = document.createElement('span');
      wrap.className = 'word';
      const inner = document.createElement('span');
      inner.textContent = w;
      wrap.appendChild(inner);
      el.appendChild(wrap);
      if (i < words.length - 1) el.appendChild(document.createTextNode(' '));
      inners.push(inner);
    });
    return inners;
  }

  /* 
     1  HERO ENTRANCE
      */
  (() => {
    const heroMedia = document.querySelector('[data-ed-hero-media]');
    const heroInners = document.querySelectorAll('.ed-hero .mask-i');
    const footInners = document.querySelectorAll('.ed-hero-foot .mask-i');

    gsap.set([...heroInners, ...footInners], { yPercent: 120 });

    const tl = gsap.timeline({ defaults: { ease: 'power4.out' } });

    if (heroMedia) {
      tl.fromTo(heroMedia, { scale: 1.15 }, { scale: 1, duration: 2.5 }, 0);
    }

    tl.to(heroInners, { yPercent: 0, duration: 1.4, stagger: 0.1 }, 0.3);
    tl.to(footInners, { yPercent: 0, duration: 1, stagger: 0.05 }, 0.8);

    // Hero parallax on scroll
    if (heroMedia) {
      gsap.to(heroMedia, {
        yPercent: 20, scale: 1.05, ease: 'none',
        scrollTrigger: {
          trigger: '.ed-hero',
          start: 'top top',
          end: 'bottom top',
          scrub: 0.5,
        },
      });
    }
  })();

  /* 
     2  PINNED SCROLL SEQUENCE
      */
  (() => {
    const section = document.getElementById('ed-pinned');
    if (!section) return;

    const frames = section.querySelectorAll('.ed-frame');
    const inner = section.querySelector('.ed-pinned-inner');
    if (frames.length < 2) return;

    // Create timeline
    const tl = gsap.timeline({
      scrollTrigger: {
        trigger: section,
        start: 'top top',
        end: 'bottom bottom',
        scrub: 0.8,
        pin: inner,
      },
    });

    // Cross-fade between frames
    frames.forEach((frame, i) => {
      if (i === 0) return; // first frame is already visible
      const prev = frames[i - 1];
      const pos = i * (1 / (frames.length - 1));

      // Fade out previous
      tl.to(prev, { opacity: 0, scale: 1.05, duration: 0.3 }, pos - 0.15);
      // Fade in current
      tl.fromTo(frame,
        { opacity: 0, scale: 0.95 },
        { opacity: 1, scale: 1, duration: 0.3 },
        pos - 0.1
      );

      // Stagger content elements
      const content = frame.querySelector('.ed-frame-content');
      if (content) {
        tl.fromTo(content.children,
          { opacity: 0, y: 30 },
          { opacity: 1, y: 0, duration: 0.2, stagger: 0.04 },
          pos - 0.05
        );
      }
    });
  })();

  /* 
     3  HORIZONTAL LOOKBOOK
      */
  (() => {
    const section = document.getElementById('ed-lookbook');
    if (!section) return;
    const track = section.querySelector('.ed-hscroll-track');
    if (!track) return;

    // Calculate how far to scroll
    const getScrollWidth = () => track.scrollWidth - window.innerWidth + 80;

    gsap.to(track, {
      x: () => -getScrollWidth(),
      ease: 'none',
      scrollTrigger: {
        trigger: section,
        start: 'top 20%',
        end: () => `+=${getScrollWidth()}`,
        scrub: 1,
        pin: true,
        anticipatePin: 1,
        invalidateOnRefresh: true,
      },
    });

    // Stagger items reveal
    gsap.fromTo(track.children,
      { opacity: 0, y: 40 },
      {
        opacity: 1, y: 0, duration: 0.8, stagger: 0.08, ease: 'power3.out',
        scrollTrigger: { trigger: section, start: 'top 80%' },
      }
    );
  })();

  /* 
     4  KINETIC TYPOGRAPHY
      */
  document.querySelectorAll('[data-split-words]').forEach(el => {
    const inners = splitWords(el);
    gsap.set(inners, { yPercent: 120, opacity: 0 });
    gsap.to(inners, {
      yPercent: 0,
      opacity: 1,
      duration: 1,
      ease: 'power4.out',
      stagger: 0.05,
      scrollTrigger: {
        trigger: el,
        start: 'top 82%',
        toggleActions: 'play none none reverse',
      },
    });
  });

  /* 
     5  PARALLAX IMAGE GRID
      */
  document.querySelectorAll('[data-parallax-y]').forEach(item => {
    const speed = parseFloat(item.dataset.parallaxY) || 0;
    gsap.fromTo(item,
      { y: -speed * 5 },
      {
        y: speed * 5,
        ease: 'none',
        scrollTrigger: {
          trigger: item,
          start: 'top bottom',
          end: 'bottom top',
          scrub: 1.5,
        },
      }
    );
  });

  // Image reveal animation for grid items
  gsap.utils.toArray('.ed-pgrid-item').forEach(item => {
    gsap.fromTo(item,
      { clipPath: 'inset(100% 0 0 0)', opacity: 0 },
      {
        clipPath: 'inset(0% 0 0 0)', opacity: 1,
        duration: 1.2, ease: 'power3.inOut',
        scrollTrigger: { trigger: item, start: 'top 85%' },
      }
    );
  });

  /* 
     6  SIMPLE FADES
      */
  gsap.utils.toArray('[data-fade]').forEach(el => {
    gsap.from(el, {
      y: 20, opacity: 0, duration: 0.8, ease: 'power3.out',
      scrollTrigger: { trigger: el, start: 'top 88%' },
    });
  });

  /* 
     7  CTA ENTRANCE
      */
  (() => {
    const cta = document.getElementById('ed-cta');
    if (!cta) return;
    const content = cta.querySelector('.ed-cta-content');
    if (!content) return;

    gsap.fromTo(content.children,
      { opacity: 0, y: 40 },
      {
        opacity: 1, y: 0, duration: 1, stagger: 0.15, ease: 'power3.out',
        scrollTrigger: { trigger: cta, start: 'top 70%' },
      }
    );

    // Background parallax
    const bg = cta.querySelector('.ed-cta-bg img');
    if (bg) {
      gsap.fromTo(bg,
        { yPercent: -10 },
        {
          yPercent: 10, ease: 'none',
          scrollTrigger: {
            trigger: cta,
            start: 'top bottom',
            end: 'bottom top',
            scrub: 1,
          },
        }
      );
    }
  })();

  /* 
     8  MARQUEE HOVER PAUSE
      */
  document.querySelectorAll('.ed-marquee').forEach(m => {
    const inner = m.querySelector('.ed-marquee-inner');
    if (!inner) return;
    m.addEventListener('mouseenter', () => inner.style.animationPlayState = 'paused');
    m.addEventListener('mouseleave', () => inner.style.animationPlayState = 'running');
  });

  /*  Refresh ScrollTrigger after load  */
  window.addEventListener('load', () => ST.refresh());

})();
